<script>
  import { onMount } from 'svelte';
  import { External, M } from '../external';
  import { formatMoney, ignore, makeWebsiteRequest, withTax } from '../Common/tools';
  import { fade } from 'svelte/transition';
  import Loader from '../Common/Loader.svelte';
  import SubscriptionPlansList from './SubscriptionPlansList.svelte';
  import Subscribe from './Subscribe.svelte';
  import AddonsList from './AddonsList.svelte';
  import Invoices from './Invoices.svelte';
  import BillingInfo from './BillingInfo.svelte';
  import ActiveAddonsList from './ActiveAddonsList.svelte';
  import PlanLimits from './PlanLimits.svelte';
  import { getSubscriptionDetails } from './Addons/addons';

  let vatRates = [];
  let plans = [];
  let currentPlan;
  let paymentProcessor;
  let loading = true;
  let isCanceled = false;
  let lastBillDate = '';
  let planRenewalDate = null;
  let nextSubscriptionRenewalPriceWithTax = 0;
  let nextChargePriceWithTax = 0;
  let nextChargeDate;
  let nextChargeIsAddonsOnly = false;
  let addonsPriceWithTax = 0;
  let planPriceWithTax = 0;
  let forceView = External.WebPlatform.getUrlQueryParameter(window.location.href, "show") || '';
  let activeView = 'overview';
  let canCancelPlanSelect = false;
  let subscribingOnPlan = null;
  let canCancel = false;
  let customBillingPage = '';

  let renderLastBillingAmount = () => {
    let lastBillingAmountStr = formatMoney((currentPlan.invoiceAmount < 0 ? -1 : 1) * currentPlan.invoiceAmount, currentPlan.currency);
    if (currentPlan.invoiceAmount < 0) {
      lastBillingAmountStr = 'refund for ' + lastBillingAmountStr;
    }
    return lastBillingAmountStr;
  };

  let getNonMeteredAddonsPrice = () => {
    return currentPlan.addons?.filter(addon => !addon.metered)
    .reduce((total, addon) => total + addon.clientPrice, 0);
  };

  let init = async () => {
    loading = true;
    let urlParams = External.WebPlatform.getUrlParameterMap(window.location.href);

    let subscriptionDetails = await getSubscriptionDetails();
    if (subscriptionDetails == null) {
      loading = false;
      return;
    }

    vatRates = subscriptionDetails.vat;
    plans = subscriptionDetails.plans;
    delete subscriptionDetails.vat;
    delete subscriptionDetails.plans;
    currentPlan = subscriptionDetails;
    recalcView();

    if (urlParams['payment-accepted'] === 'yes') {
      reportOnSubscribed(currentPlan.invoiceAmount || 0, currentPlan.currency);
    }

    customBillingPage = currentPlan.customBillingPage || '';
    if (customBillingPage) {
      customBillingPage += customBillingPage.indexOf('?') > -1 ? '&' : '?';
      customBillingPage += ['plan=', currentPlan.id, '&status=', currentPlan.status, '&userId=', External.userId, '&userName=', encodeURIComponent(External.userName), '&userEmail=', encodeURIComponent(External.userEmail), '&projectId=', External.projectId].join('');
    }
    forceView = null;
  };

  let recalcView = () => {
    canCancel = currentPlan.status === 'ACTIVE' || currentPlan.status === 'TRIAL' && currentPlan.trialEndPlan !== null;
    paymentProcessor = currentPlan.processor;
    loading = false;

    if (currentPlan.manuallyActivated) {
      canCancel = false;
    }
    isCanceled = currentPlan.status === 'EXPIRED' && currentPlan.nextBillingDate === '';

    if (currentPlan.invoiceDate) {
      lastBillDate = External.Tools.dateToString(External.Tools.ddmmyyStringToDate(currentPlan.invoiceDate));
    }

    nextSubscriptionRenewalPriceWithTax = withTax(currentPlan.nextBillingPeriodPriceNoTax, currentPlan.activeVatPercent);
    nextChargeDate = planRenewalDate = External.Tools.ddmmyyStringToDate(currentPlan.nextBillingDate);

    let nonMeteredAddonsPrice = getNonMeteredAddonsPrice();
    addonsPriceWithTax = withTax(nonMeteredAddonsPrice, currentPlan.activeVatPercent);

    nextChargePriceWithTax = planPriceWithTax = nextSubscriptionRenewalPriceWithTax;
    nextChargeIsAddonsOnly = false;

    if (addonsPriceWithTax > 0.01) {
      nextSubscriptionRenewalPriceWithTax += addonsPriceWithTax;
      nextChargePriceWithTax = nextSubscriptionRenewalPriceWithTax;

      if (currentPlan.status === 'ACTIVE' && currentPlan.billingPeriod === 'YEARLY') {
        let nextAddonBillingDate = External.Tools.ddmmyyStringToDate(currentPlan.nextAddonBillingDate);
        let daysToPlanRenewal = Math.ceil(Math.abs(planRenewalDate - nextAddonBillingDate) / (1000 * 60 * 60 * 24)); // difference in days
        if (daysToPlanRenewal > 0) { // showing the addon details as next billing date, not plan renewal details
          nextChargePriceWithTax = addonsPriceWithTax;
          nextChargeDate = nextAddonBillingDate;
          nextChargeIsAddonsOnly = true;
        }
      }
    }

    activeView = currentPlan.status === 'ACTIVE' || currentPlan.status === 'TRIAL' && currentPlan.trialEndPlan || currentPlan.plan.id ? 'overview' : 'plans';
    //activeView = (currentPlan.status !== 'ACTIVE' && currentPlan.plan.id === 0) ? 'plans' : 'overview';
    canCancelPlanSelect = currentPlan.plan.id > 0;

    if (activeView === 'overview' && window.location.pathname.endsWith('/invoices')) {
      activeView = 'invoices';
    } else if (forceView === 'invoices' || forceView === 'plans') {
      activeView = forceView;
    }
  };

  let onPlanSelected = (plan) => {
    activeView = 'subscribe';
    subscribingOnPlan = plan;
  };

  let cancelSubscribing = () => {
    activeView = (currentPlan.status !== 'ACTIVE' && currentPlan.plan.id === 0) ? 'plans' : 'overview';
    subscribingOnPlan = null;
  };

  let reportOnSubscribed = (total, currency) => {
    External.Tools.showWindow({
      title: '',
      width: 575,
      overflowY: 'hidden',
      markup: External.Templates.get('billingAcceptedMarkup'),
      showCloseButton: true,
      customClass: 'billing-payment-accepted'
    });

    if (window.Intercom) {
      window.Intercom('trackEvent', 'Subscribed', {
        plan_type: currentPlan.plan.name,
        billing_period: currentPlan.billingPeriod
      });
      window.Intercom('update', {
        user_id: External.userId,
        account_type: currentPlan.plan.name,
        account_status: 'ACTIVE'
      });
    }
    window.marketingModuleEnabled = currentPlan.hasMarketingModule === true;
    window.$body[window.marketingModuleEnabled ? 'removeClass' : 'addClass']('module-marketing-hidden');
    External.Tools.fireAnalyticsEvent('NewBuilderSubscription', total, currency);
    window.scrollTo(0, 0);
    try {
      External.WebPlatform.Events.trigger(window, External.WebPlatform.Events.WEBSITE_SUBSCRIBED, External.Tools.getEventData(total));
    } catch (e) {
      External.Log.error(e);
    }
  };

  let onSubscribed = (event) => {
    currentPlan = event.detail.plan;
    let total = event.detail.total;
    let currency = event.detail.currency;
    window.accountType = currentPlan.plan.name;
    window.accountTypeId = currentPlan.plan.id;
    window.accountStatus = 'ACTIVE';
    reportOnSubscribed(total, currency);
    recalcView();
  };

  let setAutorenew = async (renew) => {
    let response = await makeWebsiteRequest({
      action: 'saveAutoRenew',
      incrementModCount: true,
      data: {
        renew: renew ? 1 : 0
      }
    });
    if (response.ok) {
      currentPlan.recurring = renew;
      currentPlan = currentPlan;
      recalcView();
    }
  };

  let cancelSubscription = () => {
    if (!canCancel) {
      return;
    }
    External.Tools.prompt(M.CANCEL_SUBSCRIPTION_TITLE, M.CANCEL_SUBSCRIPTION_MESSAGE, async () => {
      setAutorenew(false);
    }, ignore, M.YES, M.NO, true, 'red-popup');
  };

  let onAddonsChanged = (subscriptionDetails) => {
    currentPlan = subscriptionDetails;
    recalcView();
  };

  let triggerRenewal = () => {
    let totalWithTax = currentPlan.nextBillingPeriodPriceNoTax * (1 + currentPlan.activeVatPercent / 100);
    Tools.prompt(M.RENEW, format(M.RENEW_MESSAGE, 'amount', WebPlatform.currencyWithCodeToStr(totalWithTax, currentPlan.currency, External.platformCountry)), async () => {
      let result = await makeWebsiteRequest({
        action: 'renewSubscription'
      });
      if (result.ok) {
        onSubscribed({
          detail: {
            plan: result.data,
            total: totalWithTax,
            currency: currentPlan.currency
          }
        });
      }
    });
  }

  onMount(init);
</script>

<div class="account-settings store-products analytics-root flex flex-col" style="height: calc(100vh - 57px);">

  {#if loading}
    <Loader />
  {:else}
    {#if currentPlan.customBillingPage}
      <iframe src="{customBillingPage}" style="width: 100%; height:100%; float:left;" />
    {:else if activeView === 'plans'}
      <div class="dashboard-inner-wrapper" in:fade>
        <SubscriptionPlansList {plans} {currentPlan} {canCancelPlanSelect} on:select={(event) => onPlanSelected(event.detail.plan)}
                               on:cancel={() => activeView = 'overview'} />
      </div>
    {:else}
      <div class="inner-wrapper members-table-wrapper" in:fade>
        <div class="content-wrapper members-content-wrapper dashboard-inner-wrapper">
          {#if activeView === 'subscribe'}
            <Subscribe plan={subscribingOnPlan} {currentPlan} currency={currentPlan.currency} {vatRates} {paymentProcessor}
                       on:cancel={cancelSubscribing}
                       on:subscribed={(event) => onSubscribed(event)} />
          {:else}
            <div class="members-wrapper dashboard-inner-billing mt-25">
              <ul class="upper-navigation upper-navigation-full">
                <li><a on:click={() => activeView = 'overview'} class:active={activeView === 'overview'}>{M.OVERVIEW}</a></li>
                <li><a on:click={() => activeView = 'billing'} class:active={activeView === 'billing'}>{M.BILLING_DETAILS}</a></li>
                <li><a on:click={() => activeView = 'invoices'} class:active={activeView === 'invoices'}>{M.MY_INVOICES}</a></li>
              </ul>

              {#if activeView === 'overview'}
                {#key currentPlan}
                  {#if canCancel && !currentPlan.recurring}
                    <div
                      class="flex col20 mt-25 f-left billing-overview-wrapper flex-col-mobile-768 store-section-analytics-warapper narrow-analytics-boxes flex-col-mobile">
                      <div class="analytics-box analytics-box-small">
                        <h3>
                          {M.EXPIRATION_MESSAGE_BILLING} {External.Tools.dateToString(External.Tools.ddmmyyStringToDate(currentPlan.nextBillingDate))}
                          <a class="blue-text ml-10 underline-text-hover" on:click={() => setAutorenew(true)}>{M.REACTIVATE}</a>
                        </h3>
                      </div>
                    </div>
                  {:else if currentPlan.manuallyActivated}
                    <div
                      class="flex col20 mt-25 f-left billing-overview-wrapper store-section-analytics-warapper narrow-analytics-boxes flex-col-mobile">
                      <div class="analytics-box analytics-box-small">
                      </div>
                      <div class="analytics-box analytics-box-small">
                        <h3>
                          {M.SUBSCRIPTION_PLAN}
                        </h3>
                        <h2 class="col20 f-left f-21 gray-text"><b>{currentPlan.plan.name}</b></h2>
                        <span class="f-left grey-text mt-5">{M.CHANGES_CONTACT_ADMIN}</span>
                      </div>
                      <div class="analytics-box analytics-box-small"></div>
                    </div>
                  {:else}
                    <!-- Overview -->
                    <div
                      class="flex col20 mt-25 f-left billing-overview-wrapper store-section-analytics-warapper narrow-analytics-boxes flex-col-mobile">
                      <div class="analytics-box analytics-box-small">
                        <h3>
                          {M.SUBSCRIPTION_PLAN}
                          <a class="blue-text ml-10 underline-text-hover"
                             on:click={() => activeView = 'plans'}>{currentPlan.status === 'EXPIRED' ? M.UPGRADE : M.CHANGE}</a>
                        </h3>
                        <h2 class="col20 f-left f-21 gray-text">
                          <b>
                            {
                              currentPlan.status === 'TRIAL' && currentPlan.trialEndPlan ?
                                `${currentPlan.trialEndPlan.name} (${M.TRIAL})` : currentPlan.plan.name
                            }
                          </b>
                          {#if currentPlan.status === 'EXPIRED'}
                            <span class="red-text">{M.EXPIRED}</span>
                          {:else if currentPlan.id > 0 && nextChargePriceWithTax > 0.01}
                            <span>{M['BILLING_TYPE_' + currentPlan.billingPeriod]}</span>
                          {/if}
                        </h2>
                      </div>
                      <div class="analytics-box analytics-box-small">
                        <h3>
                          {M.LAST_BILLING_DATE}
                        </h3>
                        <h2 class="col20 f-left f-28 gray-text">
                          {#if lastBillDate}
                            <b>{lastBillDate} - {renderLastBillingAmount()}</b>
                          {:else}
                            <b>-</b>
                          {/if}
                        </h2>
                      </div>
                      <div class="analytics-box analytics-box-small">
                        <h3 class="flex align-center">
                          {M.NEXT_BILLING_DATE}
                          {#if !nextChargeIsAddonsOnly && nextChargePriceWithTax >= 0.01 && addonsPriceWithTax > 0.01}
                            <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">info
                              <span class="none-soft hidden-info">
                                <h4>{M.RENEW_PRICE}: {formatMoney(nextChargePriceWithTax, currentPlan.currency)}</h4>
                                <span>
                                  {M.PLAN}: {formatMoney(planPriceWithTax, currentPlan.currency)}<br>
                                  {M.ADDONS}: {formatMoney(addonsPriceWithTax, currentPlan.currency)}
                                </span>
                              </span>
                            </span>
                          {/if}
                        </h3>
                        <h2 class="col20 f-left f-28 gray-text">
                          {#if currentPlan.status === 'EXPIRED'}
                            <span class="red-text">{M.STATUS_EXPIRED_MSG}</span>
                          {:else if nextChargePriceWithTax < 0.01}
                            <b>∞</b>
                          {:else}
                            <b>
                              {External.Tools.dateToString(nextChargeDate)}
                              - {formatMoney(nextChargePriceWithTax, currentPlan.currency)}
                            </b>
                          {/if}
                        </h2>
                      </div>
                    </div>
                    {#if currentPlan.status === 'EXPIRED'}
                      <div class="col20 f-left flex flex-col">
                        <a on:click={() => activeView = 'plans'} class="middle-button gray discard-billing flex-none mt-15 mb-0">
                          {M.REACTIVATE}
                        </a>
                      </div>
                    {:else if currentPlan.canRenewAccount}
                      <div class="col20 f-left flex flex-col">
                        <h5 class="col20 f-left ta-center mt-15">{M.ACCOUNT_EXPIRING_MESSAGE}</h5>
                        <a on:click={triggerRenewal} class="middle-button gray discard-billing flex-none mt-15 mb-0">
                          {M.RENEW}
                        </a>
                      </div>
                    {/if}
                  {/if}
                  <div class="flex f-left col20 mt-25 flex-col-mobile-768">
                    <div class="analytics-box-half detailed-box">
                      {#if !currentPlan.manuallyActivated}
                        <ActiveAddonsList subscription={currentPlan}
                                          on:change={(event) => onAddonsChanged(event.detail)} />
                      {/if}
                      <h2 class="col20 f-left f-28 gray-text">{M.MY_PLAN_LIMITS}</h2>
                      <PlanLimits subscription={currentPlan} />
                    </div>
                    <div class="analytics-box-half">
                      {#if currentPlan.manuallyActivated}
                        <ActiveAddonsList subscription={currentPlan}
                                          on:change={(event) => onAddonsChanged(event.detail)} />
                      {:else}
                        <AddonsList subscription={currentPlan}
                                    on:change={(event) => onAddonsChanged(event.detail)} />
                      {/if}
                    </div>
                  </div>
                {/key}
              {:else if activeView === 'billing'}
                <BillingInfo {currentPlan} {vatRates} {paymentProcessor} amount={0} futureAmount={nextSubscriptionRenewalPriceWithTax}
                             currency={currentPlan.currency} />
              {:else if activeView === 'invoices'}
                <Invoices />
              {/if}
            </div>
            {#if canCancel && currentPlan.recurring}
              <div class="col20 f-left mt-35 mb-35 t-center">
                <h3 class="t-center mb-15 block">{M.NEED_TO_CANCEL_SUBSCRIPTION}</h3>
                <a on:click={cancelSubscription} class="middle-button f-none red inline-block rounded-effect">{M.CANCEL_SUBSCRIPTION}</a>
              </div>
            {/if}
          {/if}
        </div>
      </div>
    {/if}
  {/if}
</div>

<style type="text/css">
  .dashboard-header,
  .dashboard-content-wrapper > .upper-navigation {
    display: none;
  }

</style>
